import React, { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { play } from "../../lib/sound";
import { useAuth } from "../../context/AuthContext";
import { Plane, Coins } from "lucide-react";
import { toast } from "sonner";

// Collezionabile "mappa viva": un aereo attraversa lo schermo; toccandolo il cliente
// riceve crediti fedeltà extra (server-authoritative, anti-abuso lato backend).
export default function MapCollectible({ active }) {
  const { user, setUser } = useAuth();
  const [plane, setPlane] = useState(null); // {nonce, y, rot}
  const [coins, setCoins] = useState([]);
  const posRef = useRef(0);
  const rafRef = useRef(null);
  const boxRef = useRef(null);
  const busyRef = useRef(false);

  // Ciclo di spawn: ogni 18-40s tenta uno spawn (solo clienti loggati).
  useEffect(() => {
    if (!active || !user || user.role !== "customer") return;
    let stop = false;
    let timer = null;
    const attempt = async () => {
      if (stop || plane) return schedule();
      try {
        const { data } = await api.get("/game/spawn");
        if (!stop && data?.allowed) {
          setPlane({ nonce: data.nonce, y: 18 + Math.random() * 46, rot: Math.random() > 0.5 ? 8 : -8 });
        }
      } catch (e) { /* ospite o errore: ignora */ }
      schedule();
    };
    const schedule = () => { if (!stop) timer = setTimeout(attempt, 150000 + Math.random() * 210000); };
    timer = setTimeout(attempt, 45000 + Math.random() * 45000);
    return () => { stop = true; clearTimeout(timer); };
    // eslint-disable-next-line
  }, [active, user, plane]);

  // Volo dell'aereo attraverso lo schermo (rAF). Se non toccato entro il tragitto, sparisce.
  useEffect(() => {
    if (!plane) return;
    posRef.current = -12;
    const el = boxRef.current;
    let last = performance.now();
    const frame = (now) => {
      const dt = Math.min(64, now - last); last = now;
      posRef.current += dt * 0.011; // ~11s per attraversare
      if (el) el.style.left = `${posRef.current}%`;
      if (posRef.current > 114) { setPlane(null); return; }
      rafRef.current = requestAnimationFrame(frame);
    };
    rafRef.current = requestAnimationFrame(frame);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [plane]);

  const grab = async () => {
    if (!plane || busyRef.current) return;
    busyRef.current = true;
    const x = posRef.current, y = plane.y;
    try {
      const { data } = await api.post("/game/collect", { nonce: plane.nonce });
      setPlane(null);
      play("credits");
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      setCoins(Array.from({ length: 6 }, (_, i) => ({ id: Date.now() + i, x, y, dx: (Math.random() - 0.5) * 60, dy: -40 - Math.random() * 50 })));
      setTimeout(() => setCoins([]), 1300);
      if (setUser && user) setUser({ ...user, credits: data.credits });
      toast.success(`+${data.reward} crediti raccolti!`, { description: `Saldo: ${data.credits} crediti` });
    } catch (e) {
      setPlane(null);
      toast.error(e.response?.data?.detail || "Bonus non più disponibile");
    } finally { busyRef.current = false; }
  };

  if (!active) return null;

  return (
    <div className="absolute inset-0 z-10 pointer-events-none overflow-hidden" aria-hidden={!plane}>
      <style>{`
        @keyframes fcnd-coin { to { transform: translate(var(--dx), var(--dy)) scale(0.6); opacity: 0; } }
        @keyframes fcnd-bob { 0%,100% { transform: translateY(0) rotate(var(--rot)); } 50% { transform: translateY(-6px) rotate(var(--rot)); } }
      `}</style>
      {plane && (
        <button
          ref={boxRef}
          data-testid="map-collectible-plane"
          onClick={grab}
          className="pointer-events-auto absolute -translate-y-1/2 group"
          style={{ top: `${plane.y}%`, left: "-12%" }}
        >
          <span className="relative flex items-center justify-center h-14 w-14 rounded-full bg-gradient-to-br from-amber-300/90 to-amber-500/90 shadow-[0_0_28px_rgba(251,191,36,0.7)] ring-2 ring-amber-200/60 transition-transform group-active:scale-90"
            style={{ animation: "fcnd-bob 1.6s ease-in-out infinite", "--rot": `${plane.rot}deg` }}>
            <Plane className="h-7 w-7 text-[#3a2600]" style={{ transform: `rotate(45deg)` }} />
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-[#00E676] border border-black/40 flex items-center justify-center">
              <Coins className="h-3 w-3 text-black" />
            </span>
          </span>
        </button>
      )}
      {coins.map((c) => (
        <span key={c.id} className="absolute h-6 w-6 rounded-full bg-amber-400 border border-amber-200 flex items-center justify-center shadow-lg"
          style={{ left: `${c.x}%`, top: `${c.y}%`, "--dx": `${c.dx}px`, "--dy": `${c.dy}px`, animation: "fcnd-coin 1.2s ease-out forwards" }}>
          <Coins className="h-3.5 w-3.5 text-[#3a2600]" />
        </span>
      ))}
    </div>
  );
}
